import { Button, IconButton } from '@billy/design-system'
import { ReactElement, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useInvoiceMultiCurrencyAccess } from '@features/invoices/hooks/useInvoiceMultiCurrencyAccess'

import { EmberRoute } from '../../../../../../../../../../../../../../enums/emberRoute'
import { useEmberRouteUrl } from '../../../../../../../../../../../../../../hooks/routing/useEmberRouteUrl'
import { depositOrFinalInvoiceEditorVariants } from '../../../../../../../../../../constants/variants/depositOrFinalInvoiceEditorVariants'
import { useInvoiceEditorForm } from '../../../../../../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorVariant } from '../../../../../../../../../../hooks/useInvoiceEditorVariant'
import { InvoiceCurrency } from './elements/InvoiceCurrency'
import { InvoiceLanguage } from './elements/InvoiceLanguage'
import * as Styled from './styles'

interface InvoiceLocalSettingsProps {
  onLanguageChange: () => void
}

export const InvoiceLocalSettings = ({ onLanguageChange }: InvoiceLocalSettingsProps): ReactElement => {
  const { t } = useTranslation()
  const { trackInvoiceEditor } = useInvoiceEditorForm()
  const { invoiceEditorVariant } = useInvoiceEditorVariant()
  const [shouldDisplayLocalSettings, setShouldDisplayLocalSettings] = useState<boolean>(false)
  const { url: settingsInvoicingUrl } = useEmberRouteUrl(EmberRoute.SettingsOrganizationInvoicing)
  const { shouldDisplayInvoiceMultiCurrency } = useInvoiceMultiCurrencyAccess()

  const isDepositOrFinalInvoiceEditor = depositOrFinalInvoiceEditorVariants.includes(invoiceEditorVariant)

  const handleToggleButtonClick = useCallback(() => {
    trackInvoiceEditor('Local Settings opened')
    setShouldDisplayLocalSettings((prev) => !prev)
  }, [trackInvoiceEditor])

  const handleLinkToGlobalSettingsButtonClick = useCallback(() => {
    trackInvoiceEditor('Local Settings - All invoice settings link clicked')
  }, [trackInvoiceEditor])

  return (
    <>
      <Styled.LocalSettingsWrapper isVisible={shouldDisplayLocalSettings}>
        <Styled.LocalSettingsContent isVisible={shouldDisplayLocalSettings}>
          <Styled.LinkToGlobalSettingsWrapper>
            <Button
              onClick={handleLinkToGlobalSettingsButtonClick}
              iconRight="squareArrowOutUpRight"
              size="md"
              to={settingsInvoicingUrl}
              variant="text"
            >
              {t('external_invoices.editor.form.field.local_settings.link.global_settings.label')}
            </Button>
          </Styled.LinkToGlobalSettingsWrapper>
          <InvoiceLanguage onLanguageChange={onLanguageChange} />
          {shouldDisplayInvoiceMultiCurrency && !isDepositOrFinalInvoiceEditor && <InvoiceCurrency />}
        </Styled.LocalSettingsContent>
      </Styled.LocalSettingsWrapper>
      <Styled.LocalSettingsDisplayTriggerWrapper>
        <IconButton
          icon={shouldDisplayLocalSettings ? 'x' : 'settings'}
          onClick={handleToggleButtonClick}
          size="sm"
          variant="outlined"
        />
      </Styled.LocalSettingsDisplayTriggerWrapper>
    </>
  )
}
